import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { forexApi, type MasterAccountVerifyResult } from "@/lib/forex-api";

const STEPS = ["账号信息", "资料校验", "确认绑定"];

export default function MasterAccountsBindPage() {
  const [params, setParams] = useSearchParams();
  const existingId = params.get('master_account_id');
  const [resumeReady, setResumeReady] = useState(!existingId);
  const [blocked, setBlocked] = useState('');
  const [bindingId, setBindingId] = useState<string | null>(null);
  const keyFor = (scope: string) => {
    const name = 'forex.master-onboarding.' + scope;
    let value = sessionStorage.getItem(name);
    if (!value) { value = crypto.randomUUID(); sessionStorage.setItem(name, value); }
    return value;
  };
  const creationKey = useRef(keyFor('create'));
  const [options, setOptions] = useState<Awaited<ReturnType<typeof forexApi.onboardingOptions>> | null>(null);
  const [platform,setPlatform]=useState<'mt4'|'mt5'>('mt4');
  const platformServers=options?.platform_servers ?? options?.servers.map(item=>({...item,platform:'mt4' as const})) ?? [];
  const servers=platformServers.filter(item=>item.platform===platform);
  const [consent, setConsent] = useState(false);
  const draftId = useRef<string | null>(null);
  const operationKey = useRef(crypto.randomUUID());
  const bindingVersion = useRef<number | null>(null);
  const credentialExpected = useRef<number | null>(null);
  useEffect(() => {
    let canceled = false;
    forexApi.onboardingOptions('master').then((value) => { if (!canceled) { setOptions(value); const first=value.servers[0]; if (first && !existingId) setForm((previous) => ({...previous,broker:first.broker,server:first.server})); } })
      .catch(() => { if (!canceled) setToast({type: 'error', msg: '自动开通选项暂不可用，请稍后重试'}); });
    return () => { canceled = true; };
  }, [existingId]);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ merchant: "钜睿量化科技", broker: "", server: "", accountNumber: "", accountType: "连接后确认", loginId: "", password: "", symbols: ["EURUSD", "GBPUSD"] });
  const suggestions = [...new Set(servers.filter((item) => item.broker === form.broker).map((item) => item.server) || [])];
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<"idle" | "verifying" | "success" | "failed">("idle");
  const [verifyDetail, setVerifyDetail] = useState<MasterAccountVerifyResult | null>(null);
  const [verifyError, setVerifyError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!existingId || draftId.current === existingId) return;
    let canceled = false;
    setResumeReady(false);
    Promise.all([forexApi.listMasterAccounts(), forexApi.listProvisioningJobs({target_type:'master_account',target_id:existingId})])
      .then(([accounts,jobs]) => {
        if (canceled) return;
        const account=accounts.items.find(item=>item.master_account_id===existingId);
        if (!account) { setBlocked('未找到主账号，请返回列表'); return; }
        if (account.terminal_count > 0 || jobs.items.some(job=>!['failed','canceled'].includes(job.status))) { setBlocked('已有 Terminal 或开通任务，请进入主账号详情查看进度'); return; }
        draftId.current=existingId;
        bindingVersion.current=account.credential_version || null;
        credentialExpected.current=bindingVersion.current;
        setBindingId(account.credential_binding_id || null);
        setPlatform(account.platform==='mt5'?'mt5':'mt4');
        setForm(previous=>({...previous,broker:account.broker,server:account.server,accountNumber:account.account_number_masked,symbols:account.supported_symbols}));
        setVerifyResult('success');setStep(1);setResumeReady(true);
      }).catch(()=>{ if (!canceled) setBlocked('账号开通状态暂不可确认，请刷新重试'); });
    return ()=>{canceled=true;};
  },[existingId]);


  const handleChange = (field: string, value: string | string[]) => {
    if (draftId.current && ['platform','broker','server','accountNumber','symbols'].includes(field)) { showToast('已保存草稿的账号身份不能更改，请返回列表重新创建','error'); return; }
    operationKey.current = crypto.randomUUID();
    credentialExpected.current = bindingVersion.current;
    if (field!=="password" && !(existingId && field==="loginId")) setVerifyResult("idle");
    setForm((p) => ({ ...p, [field]: value, ...(field === "broker" ? {server: ""} : {}) }));
  };
  const showToast = (msg: string, type: "success" | "error" = "success") => { setToast({ type, msg }); setTimeout(() => setToast(null), 3000); };

  const goNext = () => { setStep((s) => Math.min(s + 1, 2)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const goPrev = () => { setStep((s) => Math.max(s - 1, 0)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const allSymbols = ["EURUSD", "GBPUSD", "USDJPY", "AUDUSD", "NZDUSD", "USDCAD", "USDCHF", "XAUUSD", "GBPJPY", "EURJPY", "AUDNZD", "EURAUD"];

  const toggleSymbol = (sym: string) => {
    if (form.symbols.includes(sym)) {
      handleChange("symbols", form.symbols.filter((s) => s !== sym));
    } else {
      handleChange("symbols", [...form.symbols, sym]);
    }
  };

  const credentialRef = () => platform==='mt5' ? 'cred_mt5_'+creationKey.current.replace(/-/g,'') : form.loginId || `cred_${form.broker.toLowerCase().replace(/\s+/g, "_")}_${form.accountNumber}`;

  const handleVerify = async () => {
    setVerifying(true);
    setVerifyResult("verifying");
    setVerifyDetail(null);
    try {
      const result = await forexApi.verifyMasterAccount({
        platform,
        broker: form.broker,
        server: form.server,
        account_number: form.loginId || form.accountNumber,
        credential_ref: credentialRef(),
        supported_symbols: form.symbols,
      });
      setVerifyDetail(result);
      setVerifyError('');
      setVerifyResult("success");
      showToast('资料校验通过，连接状态等待 EA 上报');
    } catch (error) {
      const code = (error as {response?: {data?: {detail?: {code?: string}}}}).response?.data?.detail?.code;
      const message = ({
        MASTER_VERIFY_001: '该平台的经纪商服务器不支持主账号开通',
        MASTER_VERIFY_003: '账号格式不正确',
        MASTER_VERIFY_004: '登录 ID 不能为空',
        MASTER_VERIFY_005: '包含不支持的交易品种',
      } as Record<string, string>)[code || ''] || '资料校验暂不可用，请检查服务端状态';
      setVerifyError(message);
      setVerifyResult("failed");
      showToast(message, "error");
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async () => {
    if (!resumeReady || blocked || !options || !consent) return;
    setSubmitting(true);
    let stage='保存账号资料';
    try {
      if (!servers.some(item=>item.broker===form.broker && item.server===form.server)) throw new Error('服务器不支持 Master 自动开通');
      const account = draftId.current ? {master_account_id:draftId.current} : await forexApi.createMasterAccount({...platform==='mt5'?{platform}: {},broker:form.broker,server:form.server,account_number:form.accountNumber,credential_ref:credentialRef(),supported_symbols:form.symbols},creationKey.current);
      draftId.current=account.master_account_id;
      setParams({master_account_id:account.master_account_id},{replace:true});
      sessionStorage.removeItem('forex.master-onboarding.create');
      stage='检查已有开通任务';
      const jobs=await forexApi.listProvisioningJobs({target_type:'master_account',target_id:account.master_account_id});
      if (jobs.items.some(job=>!['failed','canceled'].includes(job.status))) {
        navigate(`/forex/master/detail?id=${account.master_account_id}`); return;
      }
      let credentialId=bindingId;
      let version=bindingVersion.current;
      if (!credentialId || form.password) {
        stage='保存托管凭证';
        const binding=await forexApi.storeMasterCredentials(account.master_account_id,{login:form.loginId || form.accountNumber,password:form.password,consent_version:options.consent_version,expected_credential_version:credentialExpected.current},operationKey.current);
        credentialId=binding.credential_binding_id; version=binding.version;
        setBindingId(credentialId); bindingVersion.current=version; credentialExpected.current=version;
        setForm(previous=>({...previous,password:''}));
      }
      if (!credentialId || !version) throw new Error('需要补充托管凭证');
      showToast('账号已接入，系统正在准备终端并验证连接');
      navigate(`/forex/master/detail?id=${account.master_account_id}`);
    } catch (error) {
      const response = (error as {response?: {data?: {error?: {code?: string}; detail?: {code?: string}}}}).response?.data;
      const code = response?.error?.code || response?.detail?.code;
      const reasons: Record<string, string> = {
        AUTO_VERSION_CONFLICT: '凭证版本已变化，请刷新后继续',
        AUTO_IDENTITY_MISMATCH: '登录账号与已保存账号不一致，请核对账号',
        AUTO_ACCOUNT_BUSY: '已有 Terminal 或活动任务，请查看账号详情',
        MASTER_CREATION_CONFLICT: '创建资料已变化，请恢复本次流程的原资料',
        AUTO_CREDENTIAL_UNAVAILABLE: '凭证托管服务暂不可用，请稍后重试',
        AUTO_RATE_LIMITED: '操作过于频繁，请稍后重试',
      };
      showToast(`${stage}未完成。${(code && reasons[code]) || '请在原账号继续操作或查看已有任务'}`, 'error');
    } finally { setSubmitting(false); }
  };

  const canProceed = (s: number): boolean => {
    if (s === 0) return !!form.accountNumber;
    if (s === 1) return verifyResult === "success" && consent && !!options && (!!bindingId || (!!form.password && !!form.loginId));
    return resumeReady && !blocked && canProceed(1);
  };

  return (
    <div className="space-y-5 max-w-[780px]">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">主账号绑定</h2>
          <p className="text-xs text-text-muted mt-0.5">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-brand text-white text-[10px] font-medium mr-2">
              步骤 {step + 1} / {STEPS.length}
            </span>
            {STEPS[step]}
          </p>
        </div>
        <button onClick={() => navigate("/forex/master-accounts")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand transition-colors cursor-pointer whitespace-nowrap"><i className="ri-arrow-left-line text-sm" />返回列表</button>
      </div>

      {blocked && <p role="alert" className="text-sm text-red-500">{blocked} <button onClick={()=>navigate(`/forex/master/detail?id=${existingId}`)}>查看详情</button></p>}
      {existingId && !resumeReady && !blocked && <p>正在确认已有账号和任务…</p>}
      {bindingId && <p className="text-sm">已有托管凭证，可直接提交开通；如需替换，请重新填写登录账号和密码。</p>}
      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => {
          const active = i === step;
          const done = i < step || (i === 1 && verifyResult === "success" && step >= 2);
          return (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${active ? "bg-brand text-white shadow-sm scale-105" : done ? "bg-emerald-100 text-emerald-600" : "bg-surface text-text-muted"}`}>
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border border-current">{done ? <i className="ri-check-line text-xs" /> : i + 1}</span>
                {s}
              </div>
              {i < 2 && <div className={`h-px flex-1 ${done ? "bg-emerald-400" : "bg-surface-border"}`} />}
            </div>
          );
        })}
      </div>

      <label className="flex items-start gap-2 text-xs text-text-secondary"><input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />授权平台托管此账号凭证，用于运行交易 EA；在缺少兼容行情源时，也可为同一账号创建行情采集终端。开通不会启用策略或创建跟随。</label>
      {/* Step content - keyed to force re-render */}
      <div key={`bind-step-${step}`} className="space-y-4">

        {step === 0 && (
          <div className="bg-white rounded-lg border border-surface-border p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-xs font-medium text-text-secondary mb-1.5">所属商家</label><select value={form.merchant} onChange={(e) => handleChange("merchant", e.target.value)} className="w-full h-9 px-3 rounded-lg border border-surface-border bg-white text-sm text-text-primary focus:border-brand/50 focus:outline-none cursor-pointer"><option>钜睿量化科技</option><option>鼎晖智能交易</option><option>华盈量化工作室</option><option>金汇策略团队</option><option>汇盈智能科技</option></select></div>
              <div><label className="block text-xs font-medium text-text-secondary mb-1.5" htmlFor="master-platform">交易平台</label><select id="master-platform" disabled={!!draftId.current} value={platform} onChange={event=>{const next=event.target.value as typeof platform;setPlatform(next);const first=platformServers.find(item=>item.platform===next);setForm(previous=>({...previous,broker:first?.broker||'',server:first?.server||'',accountNumber:'',loginId:'',password:''}));setConsent(false);setVerifyResult('idle');operationKey.current=crypto.randomUUID();creationKey.current=crypto.randomUUID();}} className="w-full h-9 px-3 rounded-lg border border-surface-border bg-white text-sm text-text-primary"><option value="mt4">MT4</option>{platformServers.some(item=>item.platform==='mt5')&&<option value="mt5">MT5</option>}</select></div><div><label className="block text-xs font-medium text-text-secondary mb-1.5">经纪商</label><select value={form.broker} onChange={(e) => handleChange("broker", e.target.value)} className="w-full h-9 px-3 rounded-lg border border-surface-border bg-white text-sm text-text-primary focus:border-brand/50 focus:outline-none cursor-pointer">{[...new Set(servers.map((item) => item.broker) || [])].map((broker) => <option key={broker}>{broker}</option>)}</select></div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1.5">服务器</label>
                <input
                  type="text"
                  list="master-account-server-suggestions"
                  value={form.server}
                  onChange={(e) => handleChange("server", e.target.value)}
                  placeholder={`输入或选择 ${platform.toUpperCase()} 服务器`}
                  autoComplete="off"
                  className="w-full h-9 px-3 rounded-lg border border-surface-border bg-white text-sm text-text-primary font-mono focus:border-brand/50 focus:outline-none"
                />
                <datalist id="master-account-server-suggestions">
                  {suggestions.map((server) => <option key={server} value={server} />)}
                </datalist>
              </div>
              <div><label className="block text-xs font-medium text-text-secondary mb-1.5">账户类型</label><input readOnly value={form.accountType} className="w-full h-9 px-3 rounded-lg border border-surface-border bg-white text-sm text-text-primary focus:border-brand/50 focus:outline-none " /></div>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">
                MT4/MT5 账号 <span className="text-red-500">*</span>
              </label>
              <input type="text" value={form.accountNumber} onChange={(e) => handleChange("accountNumber", e.target.value)} placeholder="如 80012345" className="w-full h-9 px-3 rounded-lg border border-surface-border bg-white text-sm text-text-primary font-mono placeholder:text-text-muted focus:border-brand/50 focus:outline-none" />
              {form.accountNumber && <p className="text-[10px] text-emerald-500 mt-1">账号已填写 ✓</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-2">支持交易品种（可多选）</label>
              <div className="flex flex-wrap gap-1.5">
                {allSymbols.map((sym) => (
                  <button key={sym} onClick={() => toggleSymbol(sym)} className={`px-2.5 py-1 rounded-md text-[11px] font-mono font-medium transition-all cursor-pointer whitespace-nowrap ${form.symbols.includes(sym) ? "bg-brand/10 text-brand border border-brand/30 ring-1 ring-brand/20" : "bg-surface text-text-muted border border-surface-border hover:border-brand/30"}`}>{sym}</button>
                ))}
              </div>
            </div>
            <div className="pt-2 border-t border-surface-border">
              <button onClick={goNext} disabled={!canProceed(0)} className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${canProceed(0) ? "bg-brand text-white hover:bg-brand/90 shadow-sm cursor-pointer" : "bg-surface text-text-muted cursor-not-allowed"}`}>
                <i className="ri-arrow-right-line text-base" />下一步：资料校验
              </button>
              {!canProceed(0) && <p className="text-[10px] text-amber-500 mt-1.5">请先填写 MT4/MT5 账号</p>}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="bg-white rounded-lg border border-surface-border p-6 space-y-4">
            <div className="bg-surface/50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-text-primary mb-2">账号信息确认</h3>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-text-muted">商家：</span><span className="text-text-primary font-medium ml-1">{form.merchant}</span></div>
                <div><span className="text-text-muted">经纪商：</span><span className="text-text-primary font-medium ml-1">{form.broker}</span></div>
                <div><span className="text-text-muted">服务器：</span><span className="text-text-primary font-medium font-mono ml-1">{form.server}</span></div>
                <div><span className="text-text-muted">账号：</span><span className="text-text-primary font-medium font-mono ml-1">{form.accountNumber}</span></div>
                <div className="col-span-2"><span className="text-text-muted">交易品种：</span><span className="text-text-primary font-medium ml-1">{form.symbols.join(", ")}</span></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-xs font-medium text-text-secondary mb-1.5">凭证引用 / {platform.toUpperCase()} 登录ID</label><input type="text" value={form.loginId} onChange={(e) => handleChange("loginId", e.target.value)} placeholder="用于生成 credential_ref" className="w-full h-9 px-3 rounded-lg border border-surface-border bg-white text-sm text-text-primary font-mono placeholder:text-text-muted focus:border-brand/50 focus:outline-none" /></div>
              <div><label className="block text-xs font-medium text-text-secondary mb-1.5">{platform.toUpperCase()} 密码（受保护托管）</label><input type="password" value={form.password} onChange={(e) => handleChange("password", e.target.value)} placeholder={`请输入 ${platform.toUpperCase()} 交易密码`} className="w-full h-9 px-3 rounded-lg border border-surface-border bg-white text-sm text-text-primary placeholder:text-text-muted focus:border-brand/50 focus:outline-none" /></div>
            </div>
            <div className={`rounded-lg border p-4 text-center transition-all ${verifyResult === "success" ? "bg-emerald-500/5 border-emerald-500/20" : verifyResult === "verifying" ? "bg-brand/5 border-brand/20" : verifyResult === "failed" ? "bg-red-500/5 border-red-500/20" : "bg-surface/50 border-surface-border"}`}>
              {verifyResult === "idle" && <p className="text-xs text-text-muted">这里只校验资料格式与服务端可接收性，不连接经纪商；真实身份由 Windows MT4 登录和 EA heartbeat 验证。</p>}
              {verifyResult === "verifying" && <div className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin text-brand" /><span className="text-xs text-brand">验证中...</span></div>}
              {verifyResult === "success" && <div className="flex items-center justify-center gap-2"><i className="ri-checkbox-circle-fill text-emerald-500 text-lg" /><span className="text-xs text-emerald-500 font-semibold">资料校验通过；仍需 EA heartbeat 完成运行身份验证</span></div>}
              {verifyResult === "failed" && <div className="flex items-center justify-center gap-2"><i className="ri-close-circle-fill text-red-500 text-lg" /><span className="text-xs text-red-500 font-semibold">{verifyError}</span></div>}
            </div>
            {verifyDetail && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  { label: "账号", value: verifyDetail.account_number_masked },
                  { label: "连接状态", value: verifyDetail.connection_status === 'pending' ? '等待 EA 心跳验证' : verifyDetail.connection_status },
                ].map((item) => (
                  <div key={item.label} className="bg-surface/50 rounded-md p-2.5">
                    <p className="text-[10px] text-text-muted">{item.label}</p>
                    <p className="text-xs text-text-primary font-mono mt-0.5">{item.value}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-3 pt-2 border-t border-surface-border">
              <button onClick={goPrev} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand cursor-pointer whitespace-nowrap"><i className="ri-arrow-left-line text-sm" />上一步</button>
              <button onClick={handleVerify} disabled={verifying || !form.loginId} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${verifying || !form.loginId ? "bg-amber-500/10 text-amber-500/40 cursor-not-allowed" : "bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 cursor-pointer"}`}>
                {verifying ? <><i className="ri-loader-4-line animate-spin text-sm" />校验中</> : verifyResult === "success" ? <><i className="ri-check-line text-sm" />资料已校验 ✓</> : <><i className="ri-shield-check-line text-sm" />校验资料</>}
              </button>
              <button onClick={goNext} disabled={!canProceed(1)} className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ml-auto ${canProceed(1) ? "bg-brand text-white hover:bg-brand/90 shadow-sm cursor-pointer" : "bg-surface text-text-muted cursor-not-allowed"}`}>
                <i className="ri-arrow-right-line text-base" />下一步：确认绑定
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white rounded-lg border border-surface-border p-6 space-y-4">
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
              <div className="flex items-start gap-3"><i className="ri-error-warning-fill text-amber-500 text-lg shrink-0 mt-0.5" /><div><p className="text-sm font-medium text-amber-500">确认绑定主账号</p><p className="text-xs text-text-secondary mt-1">绑定后该主账号将出现在管理列表，可关联智能体和 Terminal。</p></div></div>
            </div>
            <div className="space-y-2 bg-surface/50 rounded-lg p-4">
              {[{ label: "商家", value: form.merchant }, { label: "经纪商", value: form.broker }, { label: "服务器", value: form.server }, { label: "MT4 账号", value: verifyDetail?.account_number_masked || form.accountNumber }, { label: "账户类型", value: form.accountType }, { label: "交易品种", value: form.symbols.join(", ") }, { label: "资料校验", value: verifyDetail?.message || "已通过" }, { label: "EA 运行身份", value: "绑定后等待 heartbeat" }].map((row) => (<div key={row.label} className="flex items-center justify-between"><span className="text-xs text-text-muted">{row.label}</span><span className="text-xs text-text-primary font-mono font-medium">{row.value}</span></div>))}
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-surface-border">
              <button onClick={goPrev} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand cursor-pointer whitespace-nowrap"><i className="ri-arrow-left-line text-sm" />上一步</button>
              <button onClick={handleSubmit} disabled={submitting || !canProceed(2)} className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-sm ${submitting ? "bg-brand/20 text-brand cursor-wait" : "bg-brand text-white hover:bg-brand/90 cursor-pointer"}`}>
                {submitting ? <><i className="ri-loader-4-line animate-spin text-base" />绑定中...</> : <><i className="ri-link-m text-base" />{existingId ? "提交自动开通" : "保存并自动开通 EA"}</>}
              </button>
            </div>
          </div>
        )}

      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-[slideUp_0.3s_ease-out]">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white ${toast.type === "error" ? "bg-red-500" : "bg-emerald-500"}`}>
            <i className={`${toast.type === "error" ? "ri-error-warning-fill" : "ri-checkbox-circle-fill"} text-base`} />
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}
